import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, AlertTriangle, Database, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DevTools = () => {
  const [secretKey, setSecretKey] = useState('');
  const [seedKey, setSeedKey] = useState('');
  const [purging, setPurging] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  
  const handlePurge = async (e) => {
    e.preventDefault();
    
    if (!window.confirm('⚠️ ATTENTION : Cela va supprimer TOUTES les données de la base. Continuer ?')) {
      return;
    }
    
    try {
      setPurging(true);
      const response = await axios.post(`${API}/dev/purge-database?secret_key=${secretKey}`);
      setPurgeResult(response.data);
      toast.success('Base de données purgée avec succès !');
      setSecretKey('');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la purge');
      setPurgeResult(null);
    } finally {
      setPurging(false);
    }
  };
  
  const handleSeed = async (e) => {
    e.preventDefault();
    
    try {
      setSeeding(true);
      const response = await axios.post(`${API}/dev/seed-test-data?secret_key=${seedKey}`);
      setSeedResult(response.data);
      toast.success('Données de test créées !');
      setSeedKey('');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      setSeedResult(null);
    } finally {
      setSeeding(false);
    }
  };
  
  return (
    <div 
      className="min-h-screen py-12"
      style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
      data-testid="dev-tools-page"
    >
      <div className="container-custom max-w-2xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)' }}>
            🛠️ Dev Tools
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Outils de développement - Environnement de test uniquement
          </p>
        </div>
        
        <Alert className="bg-[var(--error)]/10 border-[var(--error)]">
          <AlertTriangle className="h-4 w-4 text-[var(--error)]" />
          <AlertDescription className="text-[var(--error)]">
            <strong>ATTENTION :</strong> Ces outils sont destinés au développement uniquement. Ne pas utiliser en production.
          </AlertDescription>
        </Alert>
        
        {/* Reset Rate Limits */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[var(--warning)]">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Réinitialiser le Rate Limiting</CardTitle>
                <CardDescription>
                  Débloquer toutes les IP et réinitialiser les compteurs de requêtes
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="border-[var(--warning)] text-[var(--warning)] hover:bg-[var(--warning)]/10"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/dev/reset-rate-limits`);
                  toast.success(`Rate limits réinitialisés — ${res.data.db_events_cleared || 0} événements DB nettoyés`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Erreur');
                }
              }}
              data-testid="reset-rate-limits-button"
            >
              Réinitialiser
            </Button>
          </CardContent>
        </Card>

        {/* Purge Database */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--error)' }}>
                <Trash2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Purger la base de données</CardTitle>
                <CardDescription>
                  Supprime toutes les données (users, contrats, heures, etc.)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePurge} className="space-y-4">
              <div>
                <Label htmlFor="secret_key">Clé secrète</Label>
                <Input
                  id="secret_key"
                  type="text"
                  placeholder="PURGE_DEV_2024"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  required
                  data-testid="secret-key-input"
                />
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  Entrez la clé secrète : <code className="bg-[var(--neutral-100)] px-1 rounded">PURGE_DEV_2024</code>
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={purging}
                style={{ backgroundColor: 'var(--error)' }}
                data-testid="purge-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {purging ? 'Purge en cours...' : 'Purger la base de données'}
              </Button>
            </form>
            
            {purgeResult && (
              <Alert className="mt-4 bg-[var(--success)]/10 border-[var(--success)]">
                <CheckCircle className="h-4 w-4 text-[var(--success)]" />
                <AlertDescription>
                  <strong>Purge réussie !</strong>
                  <div className="mt-2 text-sm">
                    {Object.entries(purgeResult.purged_counts || {}).map(([collection, count]) => (
                      <div key={collection} className="flex justify-between">
                        <span>{collection}:</span>
                        <span className="font-mono font-semibold">{count} supprimé(s)</span>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Seed Test Data */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--success)' }}>
                <Database className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Créer des données de test</CardTitle>
                <CardDescription>
                  Ajoute 1 nounou, 1 parent, 1 contrat avec code, et 3 entrées de temps
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSeed} className="space-y-4">
              <div>
                <Label htmlFor="seed_key">Clé secrète</Label>
                <Input
                  id="seed_key"
                  type="text"
                  placeholder="SEED_DEV_2024"
                  value={seedKey}
                  onChange={(e) => setSeedKey(e.target.value)}
                  required
                  data-testid="seed-key-input"
                />
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  Entrez la clé secrète : <code className="bg-[var(--neutral-100)] px-1 rounded">SEED_DEV_2024</code>
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={seeding}
                style={{ backgroundColor: 'var(--success)' }}
                data-testid="seed-button"
              >
                <Database className="mr-2 h-4 w-4" />
                {seeding ? 'Création en cours...' : 'Créer des données de test'}
              </Button>
            </form>
            
            {seedResult && (
              <Alert className="mt-4 bg-[var(--success)]/10 border-[var(--success)]">
                <CheckCircle className="h-4 w-4 text-[var(--success)]" />
                <AlertDescription>
                  <strong>Données créées !</strong>
                  <div className="mt-3 text-sm space-y-2">
                    <div className="p-2 bg-[var(--neutral-100)] rounded">
                      <strong>Nounou :</strong>
                      <div className="font-mono text-xs mt-1">
                        Email: {seedResult.data?.nanny?.email}<br/>
                        Password: {seedResult.data?.nanny?.password}
                      </div>
                    </div>
                    <div className="p-2 bg-[var(--neutral-100)] rounded">
                      <strong>Parent :</strong>
                      <div className="font-mono text-xs mt-1">
                        Email: {seedResult.data?.parent?.email}<br/>
                        Password: {seedResult.data?.parent?.password}
                      </div>
                    </div>
                    <div className="p-2 bg-[var(--primary-100)] rounded border border-[var(--primary-400)]">
                      <strong>Code de liaison :</strong>
                      <div className="font-mono text-lg font-bold mt-1 text-[var(--primary-400)]">
                        {seedResult.data?.contract?.link_code}
                      </div>
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {seedResult.data?.time_entries} entrées de temps créées
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
        

        
        {/* Email Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--info)' }}>
                <Database className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>État des emails</CardTitle>
                <CardDescription>
                  Configuration Mailjet
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">Service :</span>
              <span className="font-medium">Mailjet</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">Expéditeur :</span>
              <span className="font-medium">contact@netlyd.fr</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[var(--text-secondary)]">Nom :</span>
              <span className="font-medium">Nannyou</span>
            </div>
            <Alert className="mt-4 bg-[var(--warning)]/10 border-[var(--warning)]">
              <AlertDescription className="text-xs">
                <strong>Note :</strong> Si les emails ne s'envoient pas, vérifiez que les credentials Mailjet sont valides et que le compte n'a pas atteint sa limite.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DevTools;
